<template>
  <v-card elevation="2" outlined shaped tile class="my-4 px-2">
    <v-card-title>
      Assessment Table
      <v-spacer />
      <v-btn color="primary" small @click="openDialog()">Add</v-btn>
    </v-card-title>

    <v-data-table
      class="assessment-table"
      :headers="headers"
      :items="assessments"
      item-value="id"
      show-expand
      hide-default-footer
      expand-on-click
      single-expand
      @update:expanded="onExpanded"
    >
      <!-- Normal Row Content -->
      <template #[`item.icon`]="{ item }">
        <v-avatar size="40" class="image-border">
          <v-img :src="item.icon" alt="icon" />
        </v-avatar>
      </template>

      <template #[`item.backgroundUrl`]="{ item }">
        <v-img width="40" :src="item.backgroundUrl" alt="icon" />
      </template>

      <template #[`item.status`]="{ item }">
        <v-switch
          color="primary"
          v-model="item.status"
          true-value="active"
          false-value="inactive"
          @change="toggleStatus(item)"
        />
      </template>

      <!-- Actions -->
      <!-- <template #[`item.actions`]="{ item }">
                <v-btn icon color="green" @click.stop="moveUp(item.id)">
                    <v-icon>mdi-arrow-up-bold-box</v-icon>
                </v-btn>
                <v-btn icon color="red" @click.stop="moveDown(item.id)">
                    <v-icon>mdi-arrow-down-bold-box</v-icon>
                </v-btn>
            </template> -->

      <!-- Expanded Row -->
      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length">
          <v-form :ref="'editForm-' + item.id" v-model="Form.valid" lazy-validation>
            <v-container fluid class="my-4">
              <v-card-title> Edit Assessment </v-card-title>
              <v-card-subtitle :style="{ color: 'red' }">
                *Changes in this section require clicking the "Save" button.
              </v-card-subtitle>
              <v-row justify="end" class="mt-6">
                <v-btn color="primary" small @click="onSave(item.id)">Save</v-btn>
              </v-row>
              <v-row>
                <!-- Title & Description -->
                <v-col cols="12" md="5">
                  <v-text-field v-model="Form.title['th']" label="Title TH" :rules="[requiredRule]" dense outlined />
                  <v-text-field v-model="Form.title['en']" label="Title EN" :rules="[requiredRule]" dense outlined />
                  <v-text-field
                    v-model="Form.description['th']"
                    label="Description TH"
                    :rules="[requiredRule]"
                    dense
                    outlined
                  />
                  <v-text-field
                    v-model="Form.description['en']"
                    label="Description EN"
                    :rules="[requiredRule]"
                    dense
                    outlined
                  />
                </v-col>

                <!-- Icon -->
                <v-col cols="12" md="3" class="d-flex flex-column align-center">
                  <v-img
                    :src="selectIconImageBase64 || item.icon"
                    alt="icon"
                    max-width="200"
                    class="image-border"
                    style="cursor: pointer"
                    @click="() => openImagePicker('icon', item)"
                  />
                  <small class="grey--text text--darken-1">Icon</small>
                  <small class="grey--text text--darken-1">PNG ≤ 1MB</small>
                </v-col>

                <!-- Background Image -->
                <v-col cols="12" md="4" class="d-flex flex-column align-center">
                  <v-img
                    :src="selectImageBase64 || item.backgroundUrl"
                    alt="background"
                    max-width="200"
                    class="image-border"
                    style="cursor: pointer"
                    @click="() => openImagePicker('image', item)"
                  />
                  <small class="grey--text text--darken-1">Cover Image</small>
                  <small class="grey--text text--darken-1">PNG/JPG ≤ 1MB</small>
                </v-col>
              </v-row>

              <assessment-page
                title="Landing Pages"
                type="landing"
                :items="item.landing"
                :assessmentId="item.id"
                @onAdd="onCreatePage"
                @onUpdate="onUpdatePage"
                @onDelete="onDeletePage"
              />
              <assessment-page
                title="Result Pages"
                type="result"
                :items="item.result"
                :assessmentId="item.id"
                @onAdd="onCreatePage"
                @onUpdate="onUpdatePage"
                @onDelete="onDeletePage"
              />
              <assessment-question
                title="Questions"
                :questions="item.questions"
                :assessmentId="item.id"
                @onAdd="onCreateQuestion"
                @onUpdate="onUpdateQuestion"
                @onDelete="onDeleteQuestion"
              />

              <assessment-condition
                :resultPages="item.result"
                :conditions="item.condition"
                :assessmentId="item.id"
                @onSave="onSaveCondition"
              />

              <!-- Footer Actions -->
              <v-row justify="center" class="mt-6">
                <v-btn text color="error" small @click="onDelete(item.id)">Delete Assessment</v-btn>
              </v-row>
            </v-container>
          </v-form>
        </td>
      </template>
    </v-data-table>

    <!-- Add Assessment Modal -->
    <v-dialog v-model="dialog" max-width="700px">
      <v-card>
        <v-card-title>
          Add Assessment
          <v-spacer />
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-form ref="formRef" v-model="newForm.valid" lazy-validation>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="newForm.title.th" label="Title TH" :rules="[requiredRule]" dense outlined />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="newForm.title.en" label="Title EN" :rules="[requiredRule]" dense outlined />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newForm.description.th"
                  label="Description TH"
                  :rules="[requiredRule]"
                  dense
                  outlined
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newForm.description.en"
                  label="Description EN"
                  :rules="[requiredRule]"
                  dense
                  outlined
                />
              </v-col>

              <v-col cols="12" class="d-flex flex-column align-center mt-4">
                <v-img :src="selectNewIconBase64" max-width="200" class="image-border mb-2" />
                <v-btn small color="secondary" @click="() => openImagePicker('new-icon')">Upload Icon</v-btn>
                <v-input :value="selectNewIconBase64" :rules="[imageRequiredRule]" hide-details="auto" class="pa-0" />
              </v-col>

              <v-col cols="12" class="d-flex flex-column align-center mt-4">
                <v-img :src="selectNewImageBase64" max-width="200" class="image-border mb-2" />
                <v-btn small color="secondary" @click="() => openImagePicker('new-background')"
                  >Upload Background</v-btn
                >
                <v-input :value="selectNewImageBase64" :rules="[imageRequiredRule]" hide-details="auto" class="pa-0" />
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" :disabled="isCreateDisabled" @click="onCreateAssessment"> Create </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <input type="file" ref="imageInput" style="display: none" accept="image/*" @change="onSelectFile" />
  </v-card>
</template>

<script>
import Form from '../../../form/form';
import { srcToBase64 } from '../../../utils/stringUtil';
import { Vue } from 'vue-property-decorator';
import AssessmentPage from './AssessmentPage.vue';
import AssessmentQuestion from './AssessmentQuestion.vue';
import AssessmentCondition from './AssessmentCondition.vue';
import AssessmentService from '../../../services/assessment';
export default Vue.extend({
  components: { AssessmentPage, AssessmentQuestion, AssessmentCondition },
  props: {
    assessments: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      Form: new Form({
        data() {
          return {
            title: { th: '', en: '' },
            description: { th: '', en: '' },
            icon: '',
            backgroundUrl: '',
          };
        },
      }),
      newForm: new Form({
        data() {
          return {
            title: { th: '', en: '' },
            description: { th: '', en: '' },
            icon: '',
          };
        },
      }),
      imageRequiredRule: (v) => !!v || 'Required',
      requiredRule: (v) => (v && v.toString().trim().length > 0) || 'Required',
      headers: [
        { text: 'ID', value: 'id', align: 'center' },
        { text: 'Type', value: 'code', align: 'center' },
        { text: 'Icon', value: 'icon', align: 'center' },
        { text: 'Title TH', value: 'title[th]' },
        { text: 'Description TH', value: 'description[th]' },
        { text: 'Result Count', value: 'result.length', align: 'center' },
        { text: 'Questions Count', value: 'questions.length', align: 'center' },
        { text: 'Background', value: 'backgroundUrl', align: 'center' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: '', value: 'data-table-expand' },
      ],
      currentImageType: null,
      selectIconImageFileName: null,
      selectIconImageBase64: null,
      selectImageFileName: null,
      selectImageBase64: null,
      selectNewIconBase64: null,
      selectNewIconFileName: null,
      selectNewImageBase64: null,
      selectNewImageFileName: null,
    };
  },
  methods: {
    openDialog() {
      this.newForm = {
        title: { th: '', en: '' },
        description: { th: '', en: '' },
        icon: '',
        preview: null,
        valid: false,
      };
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
      });
    },
    async toggleStatus(item) {
      await AssessmentService.update(item.id, { status: item.status });
      this.$emit('onFetch');
    },
    async onSaveCondition(assessmentId, conditions) {
      await AssessmentService.update(assessmentId, { condition: conditions });
      this.$emit('onFetch');
    },
    async onCreateAssessment() {
      const isValid = this.$refs.formRef.validate();
      if (!isValid) return;

      const body = {
        title: this.newForm.title,
        description: this.newForm.description,
        icon: this.selectNewIconBase64
          ? {
              fileName: this.selectNewIconFileName,
              base64: srcToBase64(this.selectNewIconBase64),
            }
          : '',
        backgroundUrl: this.selectNewImageBase64
          ? {
              fileName: this.selectNewImageFileName,
              base64: srcToBase64(this.selectNewImageBase64),
            }
          : '',
      };

      await AssessmentService.create(body);
      this.$emit('onFetch');
      this.dialog = false;
    },
    onExpanded(expandedItem) {
      if (expandedItem.length > 0) {
        const item = expandedItem[0];
        if (item) this.loadItem(item);
      }
    },
    loadItem(item) {
      this.Form.title = JSON.parse(JSON.stringify(item.title));
      this.Form.description = JSON.parse(JSON.stringify(item.description));
      this.Form.icon = item.icon;
      this.Form.backgroundUrl = item.backgroundUrl;
      this.selectIconImageFileName = null;
      this.selectIconImageBase64 = null;
      this.selectImageFileName = null;
      this.selectImageBase64 = null;
      this.selectNewImageBase64 = null;
      this.selectNewImageFileName = null;
    },
    openImagePicker(type) {
      this.currentImageType = type;
      this.$refs.imageInput.click();
    },
    onSelectFile(e) {
      const files = e.target.files;
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64String = event.target.result;

          if (this.currentImageType === 'icon') {
            this.selectIconImageFileName = file.name;
            this.selectIconImageBase64 = base64String;
          } else if (this.currentImageType === 'image') {
            this.selectImageFileName = file.name;
            this.selectImageBase64 = base64String;
          } else if (this.currentImageType === 'new-icon') {
            this.selectNewIconFileName = file.name;
            this.selectNewIconBase64 = base64String;
          } else if (this.currentImageType === 'new-background') {
            this.selectNewImageFileName = file.name;
            this.selectNewImageBase64 = base64String;
          }
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
        };
        reader.readAsDataURL(file);
      } else {
        console.error('No file selected');
      }
    },
    async onCreateQuestion(body) {
      await AssessmentService.createQuestion(body);
      this.$emit('onFetch');
    },
    async onUpdateQuestion(id, body) {
      await AssessmentService.updateQuestion(id, body);
      this.$emit('onFetch');
    },
    async onDeleteQuestion(id) {
      await AssessmentService.deleteQuestion(id);
      this.$emit('onFetch');
    },
    async onCreatePage(body) {
      await AssessmentService.createPage(body);
      this.$emit('onFetch');
    },
    async onUpdatePage(id, body) {
      await AssessmentService.updatePage(id, body);
      this.$emit('onFetch');
    },
    async onDeletePage(id) {
      await AssessmentService.deletePage(id);
      this.$emit('onFetch');
    },
    async onSave(id) {
      const formRef = this.$refs['editForm-' + id];
      if (!formRef) return;

      const isValid = formRef.validate();
      if (!isValid) return;

      const body = {
        title: this.Form.title,
        description: this.Form.description,
      };

      if (this.selectImageBase64) {
        body.backgroundUrl = {
          fileName: this.selectImageFileName,
          base64: srcToBase64(this.selectImageBase64),
        };
      }

      if (this.selectIconImageBase64) {
        body.icon = {
          fileName: this.selectIconImageFileName,
          base64: srcToBase64(this.selectIconImageBase64),
        };
      }

      await AssessmentService.update(id, body);
      this.$emit('onFetch');
    },
    async onDelete(id) {
      const confirm = await this.$root.$confirm(
        'Delete B2C Assessment',
        `Are you sure you want to delete this B2C assessment? This action cannot be undone.`,
        { color: 'red' }
      );

      if (!confirm) return;

      try {
        await AssessmentService.delete(id);

        this.$emit('onFetch');

        this.$notify({
          title: 'Success',
          text: 'B2C assessment deleted successfully.',
          type: 'success',
        });
      } catch (error) {
        console.error('Error deleting B2C assessment:', error);

        this.$notify({
          title: 'Error',
          text: 'There was an error deleting the B2C assessment. Please try again.',
          type: 'error',
        });
      }
    },
  },
  computed: {
    isCreateDisabled() {
      const titleTh = (this.newForm.title?.th || '').trim();
      const titleEn = (this.newForm.title?.en || '').trim();
      const descTh = (this.newForm.description?.th || '').trim();
      const descEn = (this.newForm.description?.en || '').trim();

      return !titleTh || !titleEn || !descTh || !descEn || !this.selectNewIconBase64 || !this.selectNewImageBase64;
    },
  },
});
</script>

<style scoped>
.image-border {
  border: 1px dashed grey;
}
</style>
